import {
  ChakraVStack,
  MaudCardFrame,
  SwitchV2,
  Text,
  SMTUserPermissionsCard,
} from '@equip.health/ui';
import React from 'react';
import { SMTEvents } from '~/analytics/smtEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  SMTPermissionsDropdownOptions,
  SMTPermissionTypeValue,
} from '~/lib/constants/SMT.constants';
import {
  useGetSMTPermissionsQuery,
  useUpdateSMTPermissionsMutation,
} from '~/store/services/SMT';

interface SMTConfigurationCardProps {
  patientId: string;
}

const TopLevelId = 'SMT__treatment-configuration';

const SMTConfigurationCard = ({ patientId }: SMTConfigurationCardProps) => {
  const { isLoading, data: smtPermissions } =
    useGetSMTPermissionsQuery(patientId);

  const { trackEvent } = useAnalytics();
  const [updateSMTPermissions] = useUpdateSMTPermissionsMutation();

  const handleSMTToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    updateSMTPermissions({ patientId, smtEnabled: isChecked });
    trackEvent(SMTEvents.patientToggleClicked, {
      'Is SMT Enabled': isChecked,
    });
  };

  const updatePermissionForUser = ({
    userId,
    permissionValue,
  }: {
    userId: string;
    permissionValue: SMTPermissionType;
  }) => {
    const isSmtEnabled = smtPermissions?.smtEnabled;
    updateSMTPermissions({
      patientId,
      permissions: [
        {
          permissionType: permissionValue,
          userId,
        },
      ],
      ...(isSmtEnabled !== undefined && { smtEnabled: isSmtEnabled }),
    });
    trackEvent(SMTEvents.updatePermissionsSelected, {
      'Is SMT Enabled': isSmtEnabled,
      'Permission Type': permissionValue,
      'User ID': userId,
    });
  };

  const baseTestId = `${TopLevelId}__${patientId}`;
  const isSmtEnabled = smtPermissions?.smtEnabled ?? false;

  return (
    <ChakraVStack
      alignItems="start"
      gap="16px"
      marginBottom="16px"
      width="100%"
    >
      <Text id={`${TopLevelId}__title`} variant="h5">
        Patient recommendations
      </Text>
      <MaudCardFrame id={TopLevelId}>
        <ChakraVStack alignItems="flex-start" gap="18px">
          <SwitchV2
            id={baseTestId}
            isChecked={isSmtEnabled}
            isDisabled={isLoading}
            label="Equip Journal"
            onChange={handleSMTToggle}
            size="md"
          />
          {isSmtEnabled && (
            <ChakraVStack gap={3} w="100%">
              {smtPermissions?.permissions?.map((p) => {
                return (
                  <SMTUserPermissionsCard
                    fallbackPermissionValue={SMTPermissionTypeValue.NO_ACCESS}
                    key={p.userId}
                    onPermissionsValueSelect={(
                      permissionValue: SMTPermissionType,
                    ) =>
                      updatePermissionForUser({
                        permissionValue,
                        userId: p.userId,
                      })
                    }
                    permissionsDropdownOptions={SMTPermissionsDropdownOptions}
                    selectedPermissionValue={p.permissionType}
                    userId={p.userId}
                    userName={p.name}
                    userRole={p.relationToPatient}
                  />
                );
              })}
            </ChakraVStack>
          )}
        </ChakraVStack>
      </MaudCardFrame>
    </ChakraVStack>
  );
};

export default SMTConfigurationCard;
